/**
 * @file    home.js
 * @author  Daniel Naftzger
 * @created 2026-02-18
 * @updated 2026-03-05
 *
 * @description
 * The home page for the personal website. Assembles the hero section,
 * project gallery, skills grid, experience timeline, and contact footer
 * into a single scrollable page with sticky navigation.
 */

import { useState, useEffect, useRef } from "react";
import styles from './styles';
import data from './data';
import SectionHeader from './components/SectionHeader';
import ProjectCard from './components/ProjectCard';
import ContactSection from './components/ContactSection';
import ProjectArchModal from "./components/ProjectArchModal";


/** @type {string[]} Navigation items displayed in the top navbar. */
const NAV_ITEMS = ["About", "Projects", "Skills", "Resume", "Contact"];

/**
 * Renders the full home page layout including navbar, hero, content
 * sections, and footer.
 *
 * @returns {JSX.Element}
 */
function Home() {
  const [active, setActive] = useState("About");
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  /** @type {[Object|null, Function]} The project whose architecture modal is open, or null. */
  const [archProject, setArchProject] = useState(null);

  /**
   * True while a programmatic smooth-scroll is in progress (e.g. the user
   * clicked a nav link). While true the IntersectionObserver callback is
   * suppressed so intermediate sections don't briefly highlight.
   */
  const isScrollingTo = useRef(false);

  /**
   * On mount, triggers the entrance animation after a short delay and
   * attaches a scroll listener to toggle the navbar background.
   */
  useEffect(() => {
    setTimeout(() => setVisible(true), 100);
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /**
   * Scroll-spy: observes each section and updates the active nav link
   * based on which section is currently most visible in the viewport.
   * Uses IntersectionObserver with a rootMargin that favours the top
   * portion of the screen so the highlight switches early as the user
   * scrolls down.
   *
   * Suppressed while isScrollingTo is true to avoid flicker during
   * programmatic smooth-scrolls.
   */
  useEffect(() => {
    const sections = NAV_ITEMS.map((id) => document.getElementById(id)).filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingTo.current) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
          }
        });
      },
      {
        /* Observe intersections against a strip near the top of the viewport.
           -60% top margin pushes the trigger line down, +100% bottom margin
           effectively shrinks the detection zone to the upper ~40%. */
        rootMargin: "-20% 0px -75% 0px",
        threshold: 0,
      }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  /**
   * Smoothly scrolls the viewport to the section matching the given id
   * and updates the active nav state. Suppresses the scroll-spy observer
   * until the scroll finishes so intermediate sections don't flicker.
   *
   * Uses the 'scrollend' event where supported, with a timeout fallback
   * for browsers that don't support it yet.
   *
   * @param {string} id - The id attribute of the target section element
   */
  const scrollTo = (id) => {
    setActive(id);
    isScrollingTo.current = true;

    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

    // Re-enable the observer once scrolling settles.
    const unlock = () => { isScrollingTo.current = false; };

    if ("onscrollend" in window) {
      window.addEventListener("scrollend", unlock, { once: true });
    } else {
      // Fallback: re-enable after a generous delay
      setTimeout(unlock, 800);
    }
  };

  return (
    <div style={styles.root}>
      {/* Background grid overlay */}
      <div style={styles.gridBg} />

      {/* Sticky navbar */}
      <nav className="dn-nav" style={{ ...styles.nav, ...(scrolled ? styles.navScrolled : {}) }}>
        <span style={styles.logo} onClick={() => { scrollTo("About"); setMenuOpen(false); }}>DN</span>
        <div className="dn-nav-links" style={styles.navLinks}>
          {NAV_ITEMS.map((item) => (
            <button
              key={item}
              onClick={() => scrollTo(item)}
              style={{
                ...styles.navBtn,
                ...(active === item ? styles.navBtnActive : {}),
              }}
            >
              {item}
            </button>
          ))}
        </div>
        {/* Hamburger — hidden on desktop, shown on mobile via CSS */}
        <button
          className="dn-hamburger"
          style={styles.hamburger}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Toggle navigation menu"
        >
          <span style={{ ...styles.hamburgerLine, transform: menuOpen ? "rotate(45deg) translate(5px, 5px)" : "none" }} />
          <span style={{ ...styles.hamburgerLine, opacity: menuOpen ? 0 : 1, transform: menuOpen ? "scaleX(0)" : "none" }} />
          <span style={{ ...styles.hamburgerLine, transform: menuOpen ? "rotate(-45deg) translate(5px, -5px)" : "none" }} />
        </button>
      </nav>

      {/* Mobile full-screen menu overlay */}
      {menuOpen && (
        <div style={styles.mobileMenuOverlay}>
          {NAV_ITEMS.map((item) => (
            <button
              key={item}
              style={{
                ...styles.mobileMenuBtn,
                color: active === item ? "#e8ff47" : "#e8e8e8",
              }}
              onClick={() => { scrollTo(item); setMenuOpen(false); }}
            >
              {item}
            </button>
          ))}
        </div>
      )}

      <main className="dn-main" style={{ ...styles.main, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(24px)", transition: "all 0.7s cubic-bezier(0.16,1,0.3,1)" }}>

        {/* Hero / About */}
        <section id="About" style={styles.hero}>
          <div style={styles.heroTag}>Available for work</div>
          <h1 style={styles.heroName}>{data.name}</h1>
          <h2 style={styles.heroTitle}>{data.title}</h2>
          <p style={styles.heroTagline}>{data.tagline}</p>
          <p style={styles.heroBio}>{data.about}</p>
          <div style={styles.heroCtas}>
            <button style={styles.btnPrimary} onClick={() => scrollTo("Projects")}>View Projects</button>
            <button style={styles.btnSecondary} onClick={() => scrollTo("Contact")}>Get in Touch</button>
          </div>
        </section>

        {/* Projects */}
        <section id="Projects" className="dn-section" style={styles.section}>
          <SectionHeader label="02" title="Projects" />
          <div className="dn-project-grid" style={styles.projectGrid}>
            {data.projects.map((p, i) => (
              <ProjectCard key={i} project={p} onArchClick={setArchProject} />
            ))}
          </div>
        </section>

        {/* Skills */}
        <section id="Skills" className="dn-section" style={styles.section}>
          <SectionHeader label="03" title="Skills" />
          <div className="dn-skills-grid" style={styles.skillsGrid}>
            {data.skills.map((s, i) => (
              <div key={i} style={styles.skillGroup}>
                <div style={styles.skillCategory}>{s.category}</div>
                <div style={styles.skillItems}>
                  {s.items.map((item, j) => (
                    <span key={j} style={styles.skillChip}>{item}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Resume / Experience */}
        <section id="Resume" className="dn-section" style={styles.section}>
          <SectionHeader label="04" title="Experience" />
          <div className="dn-timeline" style={styles.timeline}>
            {data.experience.map((exp, i) => (
              <div key={i} style={styles.timelineItem}>
                <div style={styles.timelineDot} />
                <div style={styles.timelineContent}>
                  <div style={styles.timelineHeader}>
                    <span style={styles.timelineRole}>{exp.role}</span>
                    <span style={styles.timelinePeriod}>{exp.period}</span>
                  </div>
                  <div style={styles.timelineCompany}>{exp.company}</div>
                  <ul style={styles.timelineBullets}>
                    {exp.bullets.map((b, j) => (
                      <li key={j} style={styles.timelineBullet}>{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          <button style={{ ...styles.btnPrimary, marginTop: "2rem" }}>Download Full Résumé ↓</button>
        </section>

        {/* Contact — captcha-protected */}
        <section id="Contact" className="dn-section" style={styles.section}>
          <SectionHeader label="05" title="Contact" />
          <ContactSection />
        </section>

        <footer style={styles.footer}>
          <span>© 2026 {data.name}</span>
          
        </footer>
      </main>

      {/* Architecture modal — rendered outside <main> so overlay covers full viewport */}
      {archProject && (
        <ProjectArchModal
          project={archProject}
          onClose={() => setArchProject(null)}
        />
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:ital,wght@0,400;0,500;1,400&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0a0a0a; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #111; }
        ::-webkit-scrollbar-thumb { background: #333; border-radius: 2px; }
        button { cursor: pointer; border: none; background: none; }
        a { text-decoration: none; }
        .project-card:hover { transform: translateY(-4px); border-color: #e8ff47 !important; }
        .project-card:hover .proj-arrow { opacity: 1; transform: translate(2px,-2px); }

        /* ── Mobile ─────────────────────────────────────────── */
        @media (max-width: 768px) {
          /* Nav: tighter padding, hide desktop links, show hamburger */
          .dn-nav { padding: 1rem 1.25rem !important; }
          .dn-nav-links { display: none !important; }
          .dn-hamburger { display: flex !important; }

          /* Main content: tighter side padding */
          .dn-main { padding: 0 1.25rem !important; }

          /* Projects: single-column */
          .dn-project-grid {
            grid-template-columns: 1fr !important;
          }

          /* Skills: two columns instead of auto-fill */
          .dn-skills-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 1.5rem !important;
          }

          /* Timeline: tighter left indent */
          .dn-timeline {
            padding-left: 1.25rem !important;
          }
          .dn-timeline .timeline-dot {
            left: -1.6rem !important;
          }

          /* Contact box: less padding */
          .dn-contact-box {
            padding: 1.75rem !important;
          }

          /* Architecture modal: tighter padding on mobile */
          .dn-arch-modal {
            max-height: 90vh !important;
          }
          .dn-arch-modal-body {
            padding: 1.25rem !important;
          }
          .dn-arch-modal-header {
            padding: 1.25rem 1.25rem 0.75rem !important;
          }
        }

        @media (max-width: 480px) {
          /* Skills: single column on very small screens */
          .dn-skills-grid {
            grid-template-columns: 1fr !important;
          }
          /* Slightly reduce section top padding */
          .dn-section {
            padding-top: 4rem !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Home;